import { type ReactNode } from 'react'
import { cn } from '@/utils'

/**
 * Sticky page-header wrapper for pages that scroll inside AppShell's <main>
 * (or a container with the same p-4 lg:p-6 padding).
 *
 * Why not plain `sticky top-0`: overflow paints scrolling content inside the
 * scroll container's padding box, so a 0 offset leaves a 16-24px sliver of
 * moving rows visible above the pinned header. This wrapper bleeds over the
 * padding with negative margins (re-adding it as its own padding so resting
 * layout is unchanged) and pins with a matching NEGATIVE sticky top, covering
 * the strip with an opaque page-background layer.
 *
 * Children provide their own visible chrome (px/pt/pb, border-b, etc.) —
 * this component owns only the bleed/pinning mechanics. `bleedX` extends the
 * bleed horizontally for full-width containers (MarketingPage). If the
 * surrounding padding ever changes, update the -top/-mt/pt trio together.
 * (PersonaWorkshop keeps its own hand-tuned copy — its coach-rail offsets
 * are paired to its own bar's height; see the comment there.)
 */
export function StickyPageHeader({
  children,
  bleedX = false,
  className,
}: {
  children: ReactNode
  bleedX?: boolean
  className?: string
}) {
  return (
    <div
      data-slot="sticky-page-header"
      className={cn(
        'sticky -top-4 lg:-top-6 z-10 -mt-4 pt-4 lg:-mt-6 lg:pt-6 bg-background',
        bleedX && '-mx-4 lg:-mx-6',
        className
      )}
    >
      {children}
    </div>
  )
}
